// "Add to Home Screen": which steps to show for this phone, worked out from the user agent only.
// Pure (no window), so the server tests can check the whole matrix.

export type HomeScreenVariant =
  | { kind: 'ios-safari'; clip: string; step: string }
  | { kind: 'ios-chrome'; clip: string; step: string }
  | { kind: 'android'; clip: string; step: string }
  /** In-app browsers (Instagram, Facebook...), other iPhone browsers, and old iOS outside Safari. */
  | { kind: 'open-elsewhere'; os: 'ios' | 'android'; step: string }
  /** A computer: both phones' steps. */
  | { kind: 'desktop' };

export const CLIPS = {
  ios: '/clips/ios-a2hs.mp4',
  iosChrome: '/clips/ios-chrome-a2hs.mp4',
  android: '/clips/android-a2hs.mp4',
} as const;

export const STEPS = {
  ios: 'Tap Share at the bottom, then Add to Home Screen',
  iosChrome: 'Tap Share at the top, then Add to Home Screen',
  android: 'Tap the three dots at the top, then Add to Home screen',
  safari: 'Open this in Safari first',
  chrome: 'Open this in Chrome first',
} as const;

/** Apps that open links in their own browser, where Add to Home Screen isn't offered. */
const IN_APP = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger|\bLine\/|Snapchat|TikTok|musical_ly|BytedanceWebview|LinkedInApp|Twitter|\bGSA\//i;
/** iPhone browsers other than Safari and Chrome. */
const IOS_OTHER = /FxiOS|EdgiOS|OPiOS|OPT\/|YaBrowser|DuckDuckGo|Brave|Focus/i;

export const isIos = (ua: string) => /iPhone|iPad|iPod/.test(ua);
export const isAndroid = (ua: string) => /Android/.test(ua);
export const inAppBrowser = (ua: string) => IN_APP.test(ua);

/** "OS 17_2" → [17, 2]. Null when the user agent doesn't say. */
export function iosVersion(ua: string): [number, number] | null {
  const m = /OS (\d+)[_.](\d+)/.exec(ua);
  return m ? [Number(m[1]), Number(m[2])] : null;
}
const atLeast = (v: [number, number] | null, major: number, minor: number) => !!v && (v[0] > major || (v[0] === major && v[1] >= minor));

export function homeScreenVariant(ua: string): HomeScreenVariant {
  if (isIos(ua)) {
    if (inAppBrowser(ua) || IOS_OTHER.test(ua)) return { kind: 'open-elsewhere', os: 'ios', step: STEPS.safari };
    // Chrome on iPhone can add to the Home Screen from iOS 16.4.
    if (/CriOS/.test(ua)) return atLeast(iosVersion(ua), 16, 4) ? { kind: 'ios-chrome', clip: CLIPS.iosChrome, step: STEPS.iosChrome } : { kind: 'open-elsewhere', os: 'ios', step: STEPS.safari };
    // Anything else claiming iPhone without Safari's token is some other browser.
    if (!/Safari\//.test(ua)) return { kind: 'open-elsewhere', os: 'ios', step: STEPS.safari };
    return { kind: 'ios-safari', clip: CLIPS.ios, step: STEPS.ios };
  }
  if (isAndroid(ua)) {
    if (inAppBrowser(ua) || /; wv\)/.test(ua)) return { kind: 'open-elsewhere', os: 'android', step: STEPS.chrome };
    return { kind: 'android', clip: CLIPS.android, step: STEPS.android };
  }
  return { kind: 'desktop' };
}
